// DEPENDENCIES ---------------------------------------------------------------- //
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { authService } from '@features/auth/services/authService';
import { useUser } from '@store';
import config from '../../../config.frontend';

import { css } from '@emotion/css';
import { Theme, useTheme, Avatar, Chip, Menu, MenuItem, Typography, Divider, ListItemText } from '@mui/material';
import { getUtilityColor } from '../../../utils/themeUtils';

// Using react-icons as in __target__
import { BiCog, BiLogOut, BiMessageRoundedDetail, BiSolidUser, BiUser } from 'react-icons/bi';

const isDev = config.appEnv !== 'prod';

// TYPES ---------------------------------------------------------------- //

interface HeaderAvatarProps {
  onSupport?: () => void; // Kept for structural similarity, but linked Contacts component is out of scope
}

// COMPONENT ---------------------------------------------------------------- //

const HeaderAvatar = ({ onSupport }: HeaderAvatarProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const navigate = useNavigate();
  const user = useUser(); // Assumes current project's useUser() returns object with profile & claims

  const [avatarMenuAnchor, setAvatarMenuAnchor] = React.useState<null | HTMLElement>(null);
  const avatarMenuOpen = Boolean(avatarMenuAnchor);
  const handleAvatarMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchor(event.currentTarget);
  };
  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchor(null);
  };

  const handleClickProfile = () => {
    handleAvatarMenuClose();
    navigate('/profile');
  };

  const handleClickSettings = () => {
    handleAvatarMenuClose();
    navigate('/settings');
  };

  const handleClickSupport = () => {
    handleAvatarMenuClose();
    if (onSupport) onSupport(); // onSupport would trigger Contacts modal, which is out of scope
  };

  const handleClickLogout = async () => {
    handleAvatarMenuClose();
    // try { // User event writing specific to target, can be omitted for admin
    //   await authService.writeUserEvent('user.logout');
    // } catch (err) {
    //   console.log('Failed to write user event', err);
    // }
    await authService.logout();
    navigate('/login');
  };

  return (
    <>
      <div className={cls.avatarImage} onClick={handleAvatarMenuClick}>
        <Avatar
          alt={user?.profile?.display_name || user?.profile?.email || 'User'}
          src={user?.profile?.avatar_url || ''}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: getUtilityColor(theme, 'lighter'), // As per target
          }}
        >
          {/* Fallback Icon if no src/name - use react-icon from target */}
          {(!user?.profile?.avatar_url && !user?.profile?.display_name) && <BiSolidUser size={20} />}
        </Avatar>
        <div className={cls.avatarImageShadow} />
      </div>

      <Menu anchorEl={avatarMenuAnchor} open={avatarMenuOpen} onClose={handleAvatarMenuClose}>
        <MenuItem disabled sx={{ opacity: 1, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
          <ListItemText>
            <Typography variant='body1' sx={{ lineHeight: 1.25, fontWeight: 600, color: theme?.palette?.text?.primary }}>
              {user?.profile?.display_name || user?.profile?.email || 'Unknown User'}
            </Typography>
            <Typography
              variant='body2'
              sx={{ lineHeight: 1.25, fontWeight: 300, color: theme?.palette?.text?.secondary }}
            >
              {user?.profile?.email || 'Unknown Email'}
            </Typography>
            {isDev && user?.claims?.['x-hasura-default-role'] && (
              <Chip
                label={user?.claims?.['x-hasura-default-role']}
                sx={{ marginTop: 1, textTransform: `capitalize` }}
              />
            )}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleClickProfile}>
          <BiUser className={cls.menuIcon} /> {/* react-icon */}
          Profile
        </MenuItem>
        <MenuItem disabled onClick={handleClickSettings}> {/* Target had this disabled */}
          <BiCog className={cls.menuIcon} /> {/* react-icon */}
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleClickSupport}>
          <BiMessageRoundedDetail className={cls.menuIcon} /> {/* react-icon */}
          Support
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleClickLogout} style={{ color: theme?.palette?.error?.main }}>
          <BiLogOut className={cls.menuIcon} style={{ color: theme?.palette?.error?.main }} /> {/* react-icon */}
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};

// STYLES (Copied directly from __target__) //
const useStyles = (theme?: Theme) => {
  const styles = {
    avatarImage: css`
      position: relative;
      cursor: pointer;
      transition: all 0.1s ease-in-out;
      &:hover,
      &:focus,
      &:active {
        opacity: 0.875;
        transform: scale(1.1);
      }
    `,
    avatarImageShadow: css`
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 50%;
      box-shadow: 0px 0px 6px 2px ${theme?.palette?.action?.focus} inset;
      cursor: pointer;
    `,
    menuInfo: css`
      padding: 8px 16px;
    `,
    menuIcon: css`
      margin-right: 8px;
      color: ${theme?.palette?.text?.primary};
    `,
    menuDivider: css`
      height: 1px;
      margin: 6px 0;
      background-color: ${theme?.palette?.divider};
    `,
  };
  return styles;
};

// EXPORT //
export default HeaderAvatar;